import React, {
  ReactElement, useEffect, useRef, useState,
} from 'react'
import styled from 'styled-components'
import { formatCurrency } from '../../helpers/currencyHelper'
import { stripEmptyFromArray } from '../../helpers/dataTransformer'
import colors from '../../lib/colors'
import theme, { ZIndex } from '../../lib/theme'
import { CurrencyCode } from '../../types/API'
import { IColorTheme } from '../../types/itemTypes'

const Styles = styled.div`
  position: relative;
  flex: 1;
`
const SelectButton = styled.button`
  display: flex;
  justify-content: space-between;
  border: none;
  background-color: ${(props): string => props.theme.colourHighlight};
  color: ${(props): string => props.theme.colourTextOnHighlight};
  min-width: 54px;
  /* extra specificity to override inherited */
  &.price-select-button {
    font-size: 11px;
  }
  .select-button-arrow {
    border-left: 4px solid transparent;
    border-right: 4px solid transparent;
    border-top: 6px solid
      ${(props): string => props.theme.colourTextOnHighlight};
    margin-top: 2px;
    margin-left: 3px;
  }
  :hover {
    background-color: ${(props): string => props.theme.hover};
  }
  &.selected {
    background-color: ${(props): string => props.theme.hover};
  }
`
const PriceDropdown = styled.div`
  display: flex;
  flex-direction: column;
  max-height: 125px;
  border: 1px solid ${colors.darkGray};
  background-color: ${colors.white};
  border-bottom-left-radius: 4px;
  border-bottom-right-radius: 4px;
  overflow-y: scroll;
  ${theme.itemScrollbar};
  z-index: ${ZIndex.AboveContent};
  color: ${(props): string => props.theme.colourBodyText};
  position: absolute;
  left: 0;
  right: 0;
`
const DropdownItem = styled.div`
  cursor: pointer;
  padding: 6px;
  font-size: 11px;
  &.dropdown-selected {
    background-color: ${colors.gray};
  }
  :hover {
    background-color: ${(props): string => props.theme.colourHighlight};
    color: ${(props): string => props.theme.colourTextOnHighlight};
  }
`

const getValueArray = (value: string): string[] => stripEmptyFromArray(value.split(','))

export interface IValueProps {
  values: string
  currency: CurrencyCode
  selectedValue: number
  selectValue: (value: number) => void
  colorTheme: IColorTheme
}
function Value({
  values,
  currency,
  selectedValue,
  selectValue,
  colorTheme,
}: IValueProps): ReactElement {
  const valueArray = getValueArray(values)
  const [showDropdown, setShowDropdown] = useState<boolean>(false)

  const contentRef = useRef<HTMLDivElement>(null)
  const handleClickOutside = (mouseDownEvent: MouseEvent): void => {
    const ref = contentRef.current
    const { target } = mouseDownEvent
    if (target instanceof Node && ref && !ref.contains(target)) {
      setShowDropdown(false)
    }
  }
  const handleKeydown = (keyDownEvent: KeyboardEvent): void => {
    if (keyDownEvent.code === 'Escape') setShowDropdown(false)
  }
  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleKeydown)
    return (): void => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleKeydown)
    }
  }, [contentRef])

  const handleSelectValue = (value: number): void => {
    selectValue(value)
    setShowDropdown(false)
  }

  const classes = `price-select-button ${showDropdown ? 'selected' : ''}`

  return (
    <Styles ref={contentRef}>
      <SelectButton
        onClick={(): void => setShowDropdown((prevState) => !prevState)}
        theme={colorTheme}
        className={classes}
      >
        {formatCurrency(selectedValue, currency, true)}
        <div className="select-button-arrow" />
      </SelectButton>
      {showDropdown && (
        <PriceDropdown theme={colorTheme}>
          {valueArray.map((price) => {
            const value = parseFloat(price)

            return (
              <DropdownItem
                key={value}
                onClick={(): void => handleSelectValue(value)}
                className={selectedValue === value ? 'dropdown-selected' : ''}
                theme={colorTheme}
              >
                {formatCurrency(value, currency, true)}
              </DropdownItem>
            )
          })}
        </PriceDropdown>
      )}
    </Styles>
  )
}
export default Value
